import { CrisisNoticeCard, type CrisisNotice } from '../components/CrisisNotice.js';
import {
  asksForNumber,
  draftGapStep,
  getRecordFormat,
  hasStrength,
  RECORD_FORMATS,
  type CognitiveDistortion,
  type DraftGap,
  type RecordField,
  type RecordFormatKey,
  type ThoughtRecordDraft,
} from '@adhd/shared';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { RatingSlider } from '../components/RatingSlider.js';
import {
  FeelingsEditor,
  formFromDraft,
  saidSentence,
  splitLines,
  StepAsk,
  TrapPicker,
  type FeelingBefore,
} from '../components/WorksheetEditors.js';
import { SpeakAnswer } from '../components/SpeakAnswer.js';
import { TalkLive } from '../components/TalkLive.js';
import { api, ApiError } from '../lib/api.js';
import { useAi } from '../lib/ai.js';

/**
 * Thought record, one step per screen.
 *
 * The steps are data now, not markup: which fields appear, in what order, and
 * with what wording all come from the chosen format. A rejection sting and a
 * task-paralysis spiral want genuinely different worksheets, and every variant
 * still writes to the same underlying columns — so trends and belief-shift
 * maths work identically across all of them.
 *
 * Two constraints kept from before:
 * - You can save and stop at any step. Half-finished records are kept and
 *   surfaced in the log, not lost.
 * - The final step is never suggested or autocompleted. That step is where the
 *   change happens, and handing it to a model produces a tidy worksheet and no
 *   shift.
 */

/**
 * A worksheet drafted from a journal entry arrives here, filled in.
 *
 * It used to be shown on the entry as a card of read-only paragraphs with two
 * editable boxes, and saved as it stood: the evidence and the app's trap picks
 * could not be changed before saving, and the worksheet screen could not change
 * them after (13 Sep — he drafted one and could do neither). Now it opens as the
 * worksheet itself, one step at a time, every answer his to change, and the
 * last step his to write, like a conversation drafted on this screen.
 */
export interface DraftFromEntry {
  draft: ThoughtRecordDraft;
  voiceLogId: string;
}

function draftFromEntry(state: unknown): DraftFromEntry | null {
  if (!state || typeof state !== 'object') return null;
  const { draft, voiceLogId } = state as Partial<DraftFromEntry>;
  if (!draft || typeof voiceLogId !== 'string' || typeof draft.situation !== 'string') return null;
  // A draft can be sitting in the browser's history from before its questions
  // named their steps: a plain string goes on the first step. A question asking
  // for a number is dropped either way — the slider asks.
  const raw: unknown[] = Array.isArray(draft.gaps) ? draft.gaps : [];
  const gaps = raw.flatMap((g): DraftGap[] => {
    if (typeof g === 'string') return g.trim() ? [{ step: 'situation', question: g }] : [];
    if (!g || typeof g !== 'object') return [];
    const { step, question } = g as { step?: unknown; question?: unknown };
    const parsed = draftGapStep.safeParse(step);
    return parsed.success && typeof question === 'string' && question.trim() ? [{ step: parsed.data, question }] : [];
  }).filter((g) => !asksForNumber(g.question, Array.isArray(draft.emotions) ? draft.emotions.map((e) => e.emotion) : []));
  return { draft: { ...draft, gaps }, voiceLogId };
}

export function ThoughtRecordNew(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Read once: the form is filled from it on the first render and owns the
  // values from then on.
  const [fromEntry] = useState(() => draftFromEntry(location.state));
  const [initial] = useState(() => (fromEntry ? formFromDraft(fromEntry.draft) : null));
  // Hidden entirely when the server has no transcription provider, rather
  // than offered and then failing at the point someone has just spoken.
  // The spoken interview is a conversation, so it needs the top rung as well as
  // a provider. Gated on transcription alone, it stayed on offer at every
  // setting and failed only once somebody had already started talking.
  const { canConverse, canTranscribe } = useAi();
  /*
   * Two different things behind one flag.
   *
   * "Talk it through" is an interview with a model and genuinely needs the top
   * rung. Dictating an answer into a field is transcription and nothing else —
   * SpeakAnswer's own comment says "transcribe, and that is all it does", and
   * it calls exactly one endpoint to do it. Both hung off `canConverse`, so the
   * rung whose entire purpose is turning speech into text could not do it here,
   * on the screen where typing is hardest to face.
   */
  const canSpeak = canTranscribe;
  const [talking, setTalking] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Drafting produces the seven-column shape, so a draft is the usual worksheet.
  const [formatKey, setFormatKey] = useState<RecordFormatKey | null>(fromEntry ? 'standard' : null);
  const [step, setStep] = useState(0);
  const [distortions, setDistortions] = useState<CognitiveDistortion[]>([]);

  const [situation, setSituation] = useState(initial?.situation ?? '');
  const [thought, setThought] = useState(initial?.thought ?? '');
  const [emotions, setEmotions] = useState<FeelingBefore[]>(initial?.emotions ?? []);
  // The feelings the draft took from his entry: they lead the chip row. Those
  // still the app's are dashed until he rates one or takes it off.
  const [offered] = useState<string[]>(() => initial?.emotions.map((e) => e.emotion) ?? []);
  const [appFeelings, setAppFeelings] = useState<string[]>(offered);
  // The entry's own words, for "Because you said". From the fetch below; none if it fails.
  const [entryText, setEntryText] = useState<string | null>(null);
  const [evidenceFor, setEvidenceFor] = useState(initial?.evidenceFor ?? '');
  const [evidenceAgainst, setEvidenceAgainst] = useState(initial?.evidenceAgainst ?? '');
  // Blank on a journal entry's draft, as it always was: the entry's offer says
  // it won't write the part where you answer the thought.
  const [response, setResponse] = useState('');
  /*
   * A draft with no belief rating stays unrated until he gives one. The model
   * returns null when he never said how strongly he believed it (prompts.ts,
   * rule 2a), and a slider parked at 70 saved that 70 as his — from "Save and
   * come back" before the step was ever on screen, or by walking past it — and
   * the belief shift was then measured from it. A worksheet he starts himself
   * still starts at 70, where the slider is on the screen he types the thought.
   */
  const [beliefBefore, setBeliefBefore] = useState<number | null>(initial ? initial.beliefBefore : 70);
  /*
   * Null until the person moves it, then it starts where they started.
   *
   * It was `useState(70)` regardless of `beliefBefore`, so somebody who rated a
   * thought at 94% reached the last step and the screen already read "70% ↓24"
   * in green — a twenty-four point improvement they had not made, presented as
   * a win, on the step whose entire purpose is asking whether anything shifted.
   * Somebody who genuinely still believed it at 94% had to drag it UP to record
   * no change.
   *
   * Falling back to `beliefBefore` rather than to a constant is what makes "no
   * change" the thing you get by not touching it, which is the honest default
   * for a question about whether something changed.
   */
  const [beliefAfterRaw, setBeliefAfter] = useState<number | null>(null);
  const beliefAfter = beliefAfterRaw ?? beliefBefore;

  // A drafted worksheet whose entry already has one goes to that one. The draft
  // rides in the browser's history, so Back into it after saving — from the
  // crisis card's links, or past the entry — would otherwise offer to save a
  // second copy.
  useEffect(() => {
    if (!fromEntry) return;
    void api
      .getJournalEntry(fromEntry.voiceLogId)
      .then((e) => {
        if (e.linkedThoughtRecordId) navigate(`/thought-record/${e.linkedThoughtRecordId}`, { replace: true });
        else setEntryText(e.transcript ?? null);
      })
      .catch(() => {});
  }, [fromEntry, navigate]);
  const [afterIntensities, setAfterIntensities] = useState<Record<string, number>>({});
  const [picked, setPicked] = useState<string[]>(initial?.traps ?? []);
  // Which of `picked` the app chose and he has left there. Only a drafted
  // worksheet has any.
  const [appPicked, setAppPicked] = useState<string[]>(initial?.traps ?? []);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [crisis, setCrisis] = useState<CrisisNotice | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  useEffect(() => {
    void api.distortions().then(setDistortions).catch(() => {});
  }, []);

  // --- Format picker ------------------------------------------------------
  if (!formatKey) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <button
            className="btn btn-ghost btn-compact"
            style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
            onClick={() => navigate('/')}
          >
            <ArrowLeftIcon /> Cancel
          </button>
          <h1>What kind of moment is this?</h1>
          <p>They all end up in the same place. The questions just fit better.</p>
        </header>

        <div className="stack-sm stack">
          {RECORD_FORMATS.map((f) => (
            <button
              key={f.key}
              className="option"
              onClick={() => {
                setFormatKey(f.key);
                setStep(0);
              }}
            >
              <strong>{f.name}</strong>
              <span>{f.when}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const format = getRecordFormat(formatKey);
  const steps = format.steps;
  const current = steps[step]!;
  const isLast = step === steps.length - 1;

  // The draft's questions for a step. The answer step has none: the step list
  // they can name leaves it out.
  const questionsFor = (field: RecordField): string[] =>
    (fromEntry?.draft.gaps ?? []).filter((g) => g.step === field).map((g) => g.question);
  const unrated = emotions.filter((e) => !hasStrength(e));
  const anyAsk =
    steps.some((st) => questionsFor(st.field).length > 0) || beliefBefore === null || unrated.length > 0;
  const feelingsStep = steps.findIndex((st) => st.field === 'emotions');

  const valueFor = (field: string): string =>
    field === 'situation'
      ? situation
      : field === 'thought'
        ? thought
        : field === 'evidenceFor'
          ? evidenceFor
          : field === 'evidenceAgainst'
            ? evidenceAgainst
            : response;

  const setValueFor = (field: string, value: string): void => {
    if (field === 'situation') setSituation(value);
    else if (field === 'thought') setThought(value);
    else if (field === 'evidenceFor') setEvidenceFor(value);
    else if (field === 'evidenceAgainst') setEvidenceAgainst(value);
    else setResponse(value);
  };

  /**
   * Turn the conversation into a filled-in form to review.
   *
   * The draft endpoint is the existing one, and its rules are the ones that
   * matter here: it fills what the person actually said and is forbidden from
   * inventing emotions or evidence, and it never writes the balanced response —
   * that step is where the change happens and it has to be theirs.
   *
   * Nothing is saved. This populates the form; the person then reads it, fixes
   * whatever was misheard, writes the last step, and saves as normal.
   */
  async function applyConversation(turns: { who: string; text: string }[]): Promise<void> {
    // Only the person's own words go to the drafter. Feeding it the assistant's
    // side as well invites it to draft from the questions rather than from the
    // answers, and its phrasing would end up in the record.
    const transcript = turns
      .filter((t) => t.who === 'you')
      .map((t) => t.text)
      .join('\n\n')
      .trim();
    if (transcript === '') return;

    setDrafting(true);
    setError(null);
    try {
      const { draft, error: draftError } = await api.draftFromTranscript(transcript);
      if (draft) {
        // The same mapping a journal entry's draft gets (formFromDraft). The
        // traps are not taken: a conversation's worksheet has always been his
        // to pick them on.
        const form = formFromDraft(draft);
        if (form.situation) setSituation(form.situation);
        if (form.thought) setThought(form.thought);
        // Null when they never said how strongly — it stays unrated until they rate it.
        setBeliefBefore(form.beliefBefore);
        if (form.emotions.length > 0) setEmotions(form.emotions);
        // Their own closing words, if they said them. Not composed — the
        // drafter returns this only when it can quote them, and they land on
        // the step to read it before anything is saved.
        if (form.response) setResponse(form.response);
        if (form.evidenceFor) setEvidenceFor(form.evidenceFor);
        if (form.evidenceAgainst) setEvidenceAgainst(form.evidenceAgainst);
      } else if (draftError) {
        setError(draftError);
      }
      // Straight to the first question, so the whole thing gets read rather than
      // trusted. A record assembled from speech has more mishearings in it than
      // one that was typed.
      setStep(0);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : 'Could not turn that conversation into a record.',
      );
    } finally {
      setDrafting(false);
    }
  }

  if (talking) {
    return (
      <TalkLive
        formatKey={formatKey}
        onExit={() => setTalking(false)}
        onTranscript={(turns) => {
          setTalking(false);
          void applyConversation(turns);
        }}
      />
    );
  }

  if (drafting) {
    return (
      <div className="stack-lg stack" style={{ paddingTop: 'var(--space-7)' }}>
        <div className="talk-question">
          <h1>Writing it up</h1>
          <p className="muted">Putting what you said into the record, in your words.</p>
        </div>
        <div className="row" style={{ justifyContent: 'center' }}>
          <span className="spinner" />
        </div>
      </div>
    );
  }

  // Only the first two text steps are required — everything after can be left
  // blank and come back to.
  const canAdvance =
    current.field === 'situation' || current.field === 'thought'
      ? valueFor(current.field).trim().length > 0
      : true;

  async function save(complete: boolean): Promise<void> {
    setBusy(true);
    setError(null);
    // Only what this worksheet asked. A draft can fill a column the format has
    // no step for (evidence it's true, on a worksheet that never asks it), and
    // saving that would save words he was never shown.
    const asks = (field: RecordField): boolean => steps.some((s) => s.field === field);
    // Only traps the picker offers: a key the app made up is not on screen.
    const known = new Set(distortions.map((d) => d.key));
    const traps = distortions.length > 0 ? picked.filter((k) => known.has(k)) : picked;
    try {
      const created = await api.createThoughtRecord({
        format: formatKey!,
        situation: situation.trim() || '(not written down)',
        automaticThought: thought.trim() || '(not written down)',
        beliefBefore,
        beliefAfter: complete ? beliefAfter : null,
        // Only feelings with a strength he gave. One he named and never rated
        // is left out — the screen says so before he saves — rather than saved
        // with a number he did not give.
        emotions: emotions.filter(hasStrength).map((e) => ({
          emotion: e.emotion,
          intensityBefore: e.intensityBefore,
          // Left where it started, the after-strength is "no change" — the same
          // rule as the belief, and what the slider showed when he pressed finish.
          intensityAfter: complete ? (afterIntensities[e.emotion] ?? e.intensityBefore) : null,
        })),
        evidence: [
          ...(asks('evidenceFor') ? splitLines(evidenceFor) : []).map((text) => ({ side: 'for' as const, text })),
          ...(asks('evidenceAgainst') ? splitLines(evidenceAgainst) : []).map((text) => ({ side: 'against' as const, text })),
        ],
        distortions: traps,
        // The app's mark, on the ones it picked that he left there. The server
        // honours it only on a drafted worksheet.
        ...(fromEntry ? { appDistortions: traps.filter((k) => appPicked.includes(k)) } : {}),
        response: complete ? response.trim() || null : null,
        outcomeNote: null,
        source: fromEntry ? 'ai_assisted' : 'manual',
        voiceLogId: fromEntry?.voiceLogId ?? null,
        tags: [],
      });
      // Same as the journal: the automatic thought is where this turns up far
      // more often than the situation, and walking straight past it is exactly
      // what the check exists to stop.
      if (created.crisis) {
        // The draft stays in this history entry until it is replaced; the
        // crisis card links away, and Back must not bring the form back filled.
        if (fromEntry) navigate(location.pathname, { replace: true, state: null });
        setSavedId(created.id);
        setCrisis(created.crisis);
        setBusy(false);
        return;
      }
      // Replacing this page, so Back from the saved worksheet cannot land on the
      // draft again, filled in, ready to save a second copy.
      navigate(`/thought-record/${created.id}`, { replace: fromEntry !== null });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
      setBusy(false);
    }
  }

  if (crisis && savedId) {
    return (
      <div className="stack-lg stack">
        <header className="page-header">
          <h1>Saved</h1>
        </header>
        <CrisisNoticeCard
          notice={crisis}
          onDismiss={() => navigate(`/thought-record/${savedId}`, { replace: fromEntry !== null })}
        />
      </div>
    );
  }

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() =>
            step > 0
              ? setStep(step - 1)
              : fromEntry
                ? navigate(-1)
                : setFormatKey(null)
          }
        >
          <ArrowLeftIcon /> {step > 0 ? 'Back' : fromEntry ? 'Back to the entry' : 'Pick a different one'}
        </button>
        <h1>{current.title}</h1>
        <p>
          {format.name} · step {step + 1} of {steps.length}
        </p>
      </header>

      {/* Offered at the start, where the choice between typing and talking is
          actually made. Later on it would mean abandoning answers already
          written, and the whole point of it is that you have not started yet. */}
      {/* Drafted from an entry: say so where he starts reading, and what it
          could not find. Every answer after this is his to change. */}
      {fromEntry && step === 0 && (
        <div className="banner">
          Filled in from your journal entry. Change anything it got wrong — nothing is saved until you save it, and the
          last step is yours to write.
          {anyAsk && <> Where your entry didn&rsquo;t say something, that step asks.</>}
        </div>
      )}

      {canConverse && step === 0 && !fromEntry && (
        <button className="option" onClick={() => setTalking(true)}>
          <strong>Talk it through instead</strong>
          <span>
            Have a conversation about it out loud. It asks, you answer, and it writes the record
            up for you to check. No typing and no buttons while you talk.
          </span>
        </button>
      )}

      {/* --- Emotions --------------------------------------------------- */}
      {current.field === 'emotions' ? (
        <div className="stack">
          <p className="muted">
            {current.label}. Naming it precisely is part of the work — "dismissed" does more than
            "bad".
          </p>
          <StepAsk questions={questionsFor('emotions')} feelingWords={offered} entryText={entryText} />
          <FeelingsEditor
            emotions={emotions}
            offered={fromEntry ? offered : undefined}
            appPicked={fromEntry ? appFeelings : undefined}
            startAt={fromEntry ? null : 60}
            because={fromEntry && entryText ? (w) => saidSentence(entryText, w) : undefined}
            onChange={(next, app) => {
              setEmotions(next);
              setAppFeelings(app);
            }}
          />
        </div>
      ) : (
        /* --- Any text step ---------------------------------------------- */
        <div className="stack">
          <div className="field">
            <label htmlFor={current.field}>{current.label}</label>
            {current.hint && <span className="hint">{current.hint}</span>}
            <StepAsk questions={questionsFor(current.field)} feelingWords={offered} entryText={entryText} />
            <textarea
              id={current.field}
              className="textarea"
              style={isLast ? { minHeight: 140 } : undefined}
              value={valueFor(current.field)}
              onChange={(e) => setValueFor(current.field, e.target.value)}
              // No worked example on a drafted worksheet: an empty box there is
              // one the entry had nothing for, and grey text in it reads as a
              // suggested answer — on the last step, as his.
              placeholder={fromEntry ? undefined : current.placeholder}
              autoFocus
            />
          </div>

          {/* Speaking is offered per question rather than as a mode you enter,
              because the two get mixed in practice: the situation is easier to
              say than to type, and the numbers are easier to tap. Appends to
              whatever is already in the box. */}
          {canSpeak && (
            <SpeakAnswer
              hasExistingText={valueFor(current.field).trim().length > 0}
              onText={(text) => {
                const existing = valueFor(current.field).trim();
                setValueFor(current.field, existing === '' ? text : `${existing}\n\n${text}`);
              }}
            />
          )}

          {/* Belief rating sits with the thought that it rates. */}
          {current.field === 'thought' && (
            <div className="card stack-sm stack">
              <label htmlFor="belief">{format.beliefLabel}</label>
              <RatingSlider
                id="belief"
                value={beliefBefore}
                suffix="%"
                low="Not at all"
                high="Completely"
                onChange={setBeliefBefore}
              />
            </div>
          )}

          {/* Distortions are offered once, on the step that questions the
              thought — whichever step that is for this format. */}
          {current.field === 'evidenceAgainst' && distortions.length > 0 && (
            <TrapPicker
              distortions={distortions}
              picked={picked}
              appPicked={appPicked}
              onChange={(next) => {
                setPicked(next.picked);
                setAppPicked(next.appPicked);
              }}
            />
          )}

          {/* Re-rating belongs on the last step, whatever it is called. */}
          {isLast && (
            <>
              <div className="card stack-sm stack">
                {/* The thought, back on screen for the question that asks about
                    it. By this step it was written several screens ago, and
                    "the original thought" is a pronoun for something the writer
                    can no longer see — which is a hard question to answer
                    honestly, and the honesty is the whole point of the step. */}
                {thought.trim() && (
                  <p className="rerate-thought">“{thought.trim()}”</p>
                )}
                <label htmlFor="belief-after">
                  {format.key === 'stuck'
                    ? 'How true does the story feel now?'
                    : 'How much do you believe it now?'}
                </label>
                <RatingSlider
                  id="belief-after"
                  value={beliefAfter}
                  suffix="%"
                  low="Not at all"
                  high="Completely"
                  onChange={setBeliefAfter}
                  after={
                    beliefAfterRaw !== null && beliefBefore !== null && beliefAfterRaw < beliefBefore ? (
                      <>
                        {' '}
                        <span style={{ color: 'var(--positive)' }}>↓{beliefBefore - beliefAfterRaw}</span>
                      </>
                    ) : beliefAfterRaw === null && beliefBefore !== null ? (
                      <span className="faint"> · no change</span>
                    ) : null
                  }
                />
                {/* Said out loud, because expecting zero is how people decide
                    the technique "didn't work" and stop using it. */}
                <p className="faint">It rarely goes to zero. Any drop at all is the win.</p>
              </div>

              {emotions.length > 0 && (
                <section className="card stack">
                  <h3>And the feelings?</h3>
                  {emotions.filter(hasStrength).map((e) => (
                    <div className="field" key={e.emotion}>
                      <label htmlFor={`after-${e.emotion}`}>
                        {e.emotion} <span className="faint">was {e.intensityBefore}</span>
                      </label>
                      <RatingSlider
                        id={`after-${e.emotion}`}
                        value={afterIntensities[e.emotion] ?? e.intensityBefore}
                        low="Gone"
                        high="As strong as it was"
                        onChange={(v) => setAfterIntensities({ ...afterIntensities, [e.emotion]: v })}
                        after={afterIntensities[e.emotion] === undefined ? <span className="faint"> · no change</span> : null}
                      />
                    </div>
                  ))}
                  {/* No "was 60" for a strength he never gave: the feeling is
                      named, said to be left out, and one tap takes him back. */}
                  {unrated.length > 0 && (
                    <>
                      <p className="muted" style={{ margin: 0 }}>
                        {namesList(unrated.map((e) => e.emotion))} {unrated.length === 1 ? 'has' : 'have'} no strength
                        yet, so {unrated.length === 1 ? 'it won’t' : 'they won’t'} be saved.
                      </p>
                      {feelingsStep >= 0 && (
                        <button className="btn btn-secondary btn-block" onClick={() => setStep(feelingsStep)}>
                          Go back and set {unrated.length === 1 ? 'it' : 'them'}
                        </button>
                      )}
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      )}

      {error && <div className="banner banner-error">{error}</div>}

      <div className="stack-sm stack">
        {!isLast ? (
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={() => setStep(step + 1)}
            disabled={!canAdvance}
          >
            Next
          </button>
        ) : (
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={() => void save(true)}
            disabled={busy}
          >
            {busy ? <span className="spinner" /> : 'Save and finish'}
          </button>
        )}

        {step > 0 && !isLast && (
          <button className="btn btn-ghost btn-block" onClick={() => void save(false)} disabled={busy}>
            Save and come back to this
          </button>
        )}
        {step > 0 && !isLast && unrated.length > 0 && (
          <p className="faint" style={{ textAlign: 'center', margin: 0 }}>
            {namesList(unrated.map((e) => e.emotion))} {unrated.length === 1 ? 'has' : 'have'} no strength yet, so{' '}
            {unrated.length === 1 ? 'it isn’t' : 'they aren’t'} saved if you stop here.
          </p>
        )}
      </div>
    </div>
  );
}

/** "a", "a and b", "a, b and c" — each in bold. */
function namesList(names: string[]): JSX.Element {
  return (
    <>
      {names.map((n, i) => (
        <span key={n}>
          {i > 0 ? (i === names.length - 1 ? ' and ' : ', ') : ''}
          <strong>{n}</strong>
        </span>
      ))}
    </>
  );
}
